import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { motion } from "framer-motion";
import { Shield, Eye, UserCheck, Handshake, ChevronRight, Lock, Sparkles } from "lucide-react";
import { AnonymizedCard } from "@/components/AnonymizedCard";

const PUBLIC_ORIGIN =
    (typeof window !== "undefined" && window.location?.origin) ||
    "https://simonaratalent.us";
const CANONICAL_URL = `${PUBLIC_ORIGIN}/how-it-works`;
const OG_IMAGE = `${PUBLIC_ORIGIN}/og-how-it-works.png`;
const PAGE_TITLE = "How Simonara protects your identity — and gets you hired";
const FULL_TITLE = "How Simonara protects your identity — and gets you hired · Simonara";
const PAGE_DESC =
    "Simonara shows employers a verified, anonymized Talent Passport — skills, verifications, background — before your name, email, or phone. You consent before any identity is unlocked.";

const JSON_LD_FAQ = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: [
        {
            "@type": "Question",
            name: "How is my identity kept anonymous on Simonara?",
            acceptedAnswer: {
                "@type": "Answer",
                text: "Your name, email, and phone are stripped from the profile employers see. They evaluate you on skills, verified experience, and verifications — never on personal details. Only when you consent does an employer receive your real identity.",
            },
        },
        {
            "@type": "Question",
            name: "What is a Talent Passport?",
            acceptedAnswer: {
                "@type": "Answer",
                text: "A Talent Passport is a Simonara-verified profile containing your role, skills, experience, and a verification checklist of independently checked identity, employment, education, and reference signals. It replaces the resume as the object employers evaluate.",
            },
        },
        {
            "@type": "Question",
            name: "When do employers see my name and contact?",
            acceptedAnswer: {
                "@type": "Answer",
                text: "Never automatically. An employer expresses interest through a Simonara concierge, you review who they are and the opportunity, and you explicitly consent before any identity is shared.",
            },
        },
        {
            "@type": "Question",
            name: "Is Simonara free for job seekers?",
            acceptedAnswer: {
                "@type": "Answer",
                text: "Yes — building and sharing a Talent Passport is free. Optional paid verifications (like Personal Background Checks) exist for talent who want an extra trust signal but are never required.",
            },
        },
    ],
};

const JSON_LD_HOWTO = {
    "@context": "https://schema.org",
    "@type": "HowTo",
    name: "How Simonara protects your identity while getting you hired",
    description: PAGE_DESC,
    step: [
        { "@type": "HowToStep", position: 1, name: "Build a verified Talent Passport", text: "You build one Talent Passport — Simonara verifies identity, employment, and education once." },
        { "@type": "HowToStep", position: 2, name: "Employers see anonymized cards", text: "Your name, email, and phone are stripped. Employers evaluate skills + verifications only." },
        { "@type": "HowToStep", position: 3, name: "Simonara concierges the intro", text: "Interested employers pitch through Simonara admins. You review who they are before any reveal." },
        { "@type": "HowToStep", position: 4, name: "You unlock identity on your terms", text: "Only when you consent does the employer receive your real name and contact." },
    ],
};

// Marketing landing that turns Simonara's anonymization + consent flow
// into a top-of-funnel asset. Renders the SAME AnonymizedCard component
// the Talent Passport preview modal uses so what visitors see here is
// what employers actually see in production.
const DEMO_CARD = {
    id: "demo_hiw",
    initials: "AK",
    desired_position: "Senior Product Designer",
    city: "Los Angeles, CA",
    years_experience: 7,
    availability: "Within 1 month",
    remote_preferences: "Hybrid",
    skills: ["Figma", "Design Systems", "User Research", "Prototyping", "Motion", "Accessibility"],
    work_authorization: "US Citizen",
    trust_score: 82,
    trust_breakdown: {
        tiles: [
            { label: "Identity",   state: "verified" },
            { label: "Employment", state: "verified" },
            { label: "Education",  state: "verified" },
            { label: "References", state: "pending" },
            { label: "Background", state: "not_requested" },
            { label: "License",    state: "not_available" },
        ],
    },
};

const Step = ({ icon: Icon, tone, title, body, index }) => (
    <motion.div
        initial={{ opacity: 0, y: 6 }} whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-40px" }}
        transition={{ delay: index * 0.08 }}
        className="card-soft p-5 flex items-start gap-4"
    >
        <div className={`w-11 h-11 rounded-full flex items-center justify-center shrink-0 ${tone}`}>
            <Icon size={20} />
        </div>
        <div className="min-w-0">
            <div className="text-[10px] uppercase tracking-wider font-bold text-muted-foreground">Step {index + 1}</div>
            <div className="mt-0.5 font-heading font-semibold text-lg text-foreground">{title}</div>
            <div className="mt-1 text-sm text-muted-foreground leading-relaxed">{body}</div>
        </div>
    </motion.div>
);

export default function HowItWorks() {
    // Set page title imperatively — react-helmet-async v3 has an edge case
    // with static <title> children under React 18 StrictMode that leaves the
    // head title empty. Directly setting document.title dodges it entirely
    // and social crawlers (which drive most SEO value) still read OG tags below.
    useEffect(() => {
        const prev = document.title;
        document.title = FULL_TITLE;
        return () => { document.title = prev; };
    }, []);

    return (
        <div data-testid="how-it-works-page" className="max-w-5xl mx-auto px-4 py-14">
            <Helmet>
                <meta name="description" content={PAGE_DESC} />
                <meta name="robots" content="index, follow, max-image-preview:large" />
                <link rel="canonical" href={CANONICAL_URL} />

                {/* Open Graph */}
                <meta property="og:type" content="website" />
                <meta property="og:site_name" content="Simonara" />
                <meta property="og:title" content={PAGE_TITLE} />
                <meta property="og:description" content={PAGE_DESC} />
                <meta property="og:url" content={CANONICAL_URL} />
                <meta property="og:image" content={OG_IMAGE} />
                <meta property="og:image:width" content="1200" />
                <meta property="og:image:height" content="630" />
                <meta property="og:image:alt" content="Simonara — the anonymized talent marketplace" />

                {/* Twitter */}
                <meta name="twitter:card" content="summary_large_image" />
                <meta name="twitter:title" content={PAGE_TITLE} />
                <meta name="twitter:description" content={PAGE_DESC} />
                <meta name="twitter:image" content={OG_IMAGE} />

                <script type="application/ld+json">{JSON.stringify(JSON_LD_FAQ)}</script>
                <script type="application/ld+json">{JSON.stringify(JSON_LD_HOWTO)}</script>
            </Helmet>
            <div className="text-center max-w-2xl mx-auto">
                <p className="overline text-sky-600 dark:text-sky-400">Anonymization + Consent</p>
                <h1 className="mt-2 font-heading font-medium text-4xl sm:text-5xl tracking-tight text-foreground">
                    How Simonara protects your identity — <span className="text-emerald-600 dark:text-emerald-400">and gets you hired</span>
                </h1>
                <p className="mt-4 text-lg text-muted-foreground">
                    Employers see your <strong>skills, verifications, and background</strong> before they ever see your name.
                    You decide who unlocks your contact info — after you&apos;ve reviewed <em>them</em>.
                </p>
                <div className="mt-6 flex items-center justify-center gap-3">
                    <Link
                        to="/"
                        data-testid="how-it-works-signup-cta"
                        className="inline-flex items-center gap-1 rounded-full bg-emerald-600 hover:bg-emerald-700 text-white px-5 py-2 text-sm font-semibold"
                    >
                        Build my passport <ChevronRight size={14} />
                    </Link>
                    <a
                        href="#example"
                        className="text-sm text-muted-foreground hover:text-foreground font-semibold"
                    >
                        See an example ↓
                    </a>
                </div>
            </div>

            <section id="example" className="mt-16">
                <div className="text-center max-w-2xl mx-auto">
                    <p className="overline">Example</p>
                    <h2 className="mt-1 font-heading font-medium text-2xl sm:text-3xl">This is exactly what employers see</h2>
                    <p className="mt-3 text-sm text-muted-foreground">
                        No name. No email. No phone. Employers judge on skills + verified trust — not vibes.
                    </p>
                </div>
                <div className="mt-8 max-w-lg mx-auto">
                    <AnonymizedCard card={DEMO_CARD} testId="how-it-works-demo-card" />
                    <p className="text-[11px] text-muted-foreground mt-3 text-center">
                        <Lock size={11} className="inline mb-0.5" /> Full identity is hidden until <strong>you</strong> consent — not the employer.
                    </p>
                </div>
            </section>

            <section className="mt-16 grid gap-4 sm:grid-cols-2">
                <Step index={0} icon={Shield}    tone="bg-sky-500/15 text-sky-600 dark:text-sky-400"
                    title="Passport, not resume"
                    body="You build one verified Talent Passport — Simonara checks the facts (identity, employment, education) once and vouches for them across every employer."
                />
                <Step index={1} icon={Eye}       tone="bg-emerald-500/15 text-emerald-600 dark:text-emerald-400"
                    title="Employers see anonymized cards"
                    body="Your name, email, and phone are stripped before employers can search. They evaluate the signal, not your inbox."
                />
                <Step index={2} icon={UserCheck} tone="bg-violet-500/15 text-violet-600 dark:text-violet-400"
                    title="Simonara concierges the intro"
                    body="Interested employers pitch through Simonara admins. You review WHO they are + the role before your identity is shared."
                />
                <Step index={3} icon={Handshake} tone="bg-amber-500/15 text-amber-600 dark:text-amber-400"
                    title="You choose to unlock"
                    body="Only when you say yes does the employer get your name and contact. You control every intro — always."
                />
            </section>

            <section className="mt-16 card-soft border-emerald-500/40 bg-emerald-500/[0.05] p-6 sm:p-8 text-center">
                <Sparkles size={20} className="mx-auto text-emerald-600 dark:text-emerald-400" />
                <h2 className="mt-2 font-heading font-medium text-2xl">Ready to be search-visible without the spam?</h2>
                <p className="mt-2 text-sm text-muted-foreground max-w-xl mx-auto">
                    Build your Talent Passport in 15 minutes. Your card goes live the moment you cross 40% complete.
                </p>
                <Link
                    to="/"
                    data-testid="how-it-works-footer-cta"
                    className="mt-4 inline-flex items-center gap-1 rounded-full bg-emerald-600 hover:bg-emerald-700 text-white px-5 py-2 text-sm font-semibold"
                >
                    Get started <ChevronRight size={14} />
                </Link>
            </section>
        </div>
    );
}
